// Quality API
import api, { fetchAllPages, type AxiosResponse, type PaginatedResponse } from './client';
import type {
  QualityMetric,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  NCRTechnicalReview,
  InspectionRecord,
  QualityDashboardStats,
  QualityDashboardSummary,
  InspectionPassRate,
  Concession,
  ConcessionDocument,
  ConcessionMentionOptions,
  ConcessionComment,
  ConcessionActionLog,
  ConcessionDashboardStats,
  SourceOfRejection,
  CommonisedCorrectiveAction,
  FaultCodeGroup,
  FaultCodeCategory,
  ApprovalGroup,
  Accreditation,
  QueryParams,
} from './types';
import { qualityClaimsAPI } from './qualityClaims';
import { qualityApprovalsAPI } from './qualityApprovals';

// CR-5887 (TEMPORARY — remove with the test trigger): response shape of the
// admin-only daily Quality summary "send now" endpoint.
export interface DailyQualitySummaryTriggerResult {
  status: string;
  mode: 'single' | 'all';
  to?: string;
  result: {
    sent?: number;
    role?: string | null;
    reason?: string | null;
    emails_sent?: number;
    emails_skipped?: number;
    customers_processed?: number;
    suppliers_processed?: number;
  };
}

/** CR-7139: an NCR's root-cause chains as the ordered lists of Whys the user
 *  has — one to ten each. The NCR detail response carries them (the first five
 *  are also why_1..5 / escape_why_1..5); save and submit_rca take them. */
export interface NCRWhyChains {
  whys?: string[];
  escape_whys?: string[];
}

/** The customer's 8D CAR grades (1-5). CR-7357: four dimensions — ORC
 *  (rc_score), ERC (erc_score), OPA (ca_score), EPA (epa_score). Field names
 *  kept for ORC/OPA so the API is unchanged. */
/** Decision 3: what a goods receipt delivered of the NCR's part, and what is
 * left to reject. `delivered` is null when there is nothing to measure. */
/** CR-7485 rework: what the GRN's PO line still owes, for Shortage Qty. */
export interface ShortageAvailability {
  ordered: number;
  delivered: number;
  short_elsewhere: number;
  available: number;
}

export interface RejectAvailability {
  delivered: number | null;
  rejected_elsewhere?: number;
  available?: number;
  /** Null when the receipt has no PO line to measure against. */
  shortage?: ShortageAvailability | null;
}

export interface CarScores {
  rc_score?: number;
  erc_score?: number;
  ca_score?: number;
  epa_score?: number;
}

export const qualityAPI = {
  // Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: QualityMetric[] }>> =>
    api.get('/quality/metrics/', { params }),

  getDashboardSummary: (): Promise<AxiosResponse<QualityDashboardSummary>> =>
    api.get('/quality/metrics/dashboard_summary/'),

  // NCRs
  getNCRs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<NonConformanceReport>>> =>
    api.get('/quality/ncrs/', { params }),

  /**
   * Batched quick-filter chip counts: one request replaying each chip's exact
   * list params through the NCR list queryset (mirrors purchasing/lines/tab-counts/).
   */
  getNCRTabCounts: (tabs: Record<string, Record<string, string | number | boolean>>): Promise<AxiosResponse<{ counts: Record<string, number | null> }>> =>
    api.post('/quality/ncrs/tab-counts/', { tabs }),

  getNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.get(`/quality/ncrs/${id}/`),

  createNCR: (data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post('/quality/ncrs/', data),

  updateNCR: (id: number, data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.patch(`/quality/ncrs/${id}/`, data),

  /**
   * CR-7359: download one NCR as a PDF.
   *
   * The server renders what this user is allowed to see — suppliers get the
   * same document without internal comments, and an NCR outside their scope
   * 404s. Returns the raw blob; the caller saves it.
   *
   * @param id - the NCR's id
   * @returns the PDF as a Blob response
   */
  downloadNCRPdf: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/quality/ncrs/${id}/pdf/`, { responseType: 'blob' }),

  /**
   * Delete an NCR. Customer admins only, and only NCRs raised by hand in the
   * portal — the API rejects imported ones, which the ERP still owns.
   */
  deleteNCR: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/ncrs/${id}/`),

  /**
   * Hide an NCR from the working lists without deleting it. Customer admins
   * only; reversible via {@link unarchiveNCR}; metrics are unaffected.
   */
  archiveNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/archive/`),

  /** Restore an archived NCR to the working lists. Customer admins only. */
  unarchiveNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/unarchive/`),

  // CR-3396: Accept view context params for hierarchical filtering
  getNCRStats: (params?: Record<string, string | number>): Promise<AxiosResponse<QualityDashboardStats>> =>
    api.get('/quality/ncrs/dashboard_stats/', { params }),

  // 24 Sep 2026: approve the CAR — the 8D is complete, the NCR stays open
  // until every other required process is done. Every grade is required.
  approve8D: (id: number, scores?: CarScores): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/approve-8d/`, scores ?? {}),

  // Decision 3: how many parts on a goods receipt can still be rejected.
  // `ncr` is the NCR being edited, left out of "rejected elsewhere".
  getRejectAvailability: (grn: number, ncr?: number): Promise<AxiosResponse<RejectAvailability>> =>
    api.get('/quality/ncrs/reject-availability/', { params: ncr ? { grn, ncr } : { grn } }),

  // Close the NCR once every required process is complete. From CAR
  // Submitted it also approves the 8D, so the grades are then required.
  closeNCR: (id: number, scores?: CarScores): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/close_ncr/`, scores ?? {}),

  // Persist the RC/CA grades on their own (no approve/reject decision), so the
  // register column + 8D Score (= ORC+ERC+OPA+EPA) update as soon as the customer scores
  // the CAR — even if Approve is still gated by collection/credit workflows.
  setCarScores: (id: number, scores: CarScores): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/set-car-scores/`, scores),

  // CR-6024: customer rejects a submitted CAR and sends it back for rework.
  // NCR transitions car_submitted -> ca_submitted; supplier sees the reason
  // on the Corrective Action tab and can amend then resubmit. Accepts the
  // same optional RC/CA scores as closeNCR.
  rejectCAR: (id: number, rejectionReason: string, scores?: CarScores): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reject_car/`, { rejection_reason: rejectionReason, ...(scores ?? {}) }),

  // NCR Workflow Actions
  submitDetail: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_detail/`),

  submitRCA: (id: number, data: {
    /** CR-7139: the Occurrence Whys in order, one to ten. */
    whys: string[];
    selected_root_cause: string;
    // CR-7353: the Escape root-cause chain.
    escape_whys?: string[];
    escape_selected_root_cause?: string;
    immediate_corrective_action: string;
    disposition_instructions?: string;
    // W7: the D1 team fields on the RCA form.
    team_leader?: string;
    team_members?: string;
    team_formation_date?: string | null;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_rca/`, data),

  submitCAR: (id: number, data: {
    corrective_action: string;
    preventive_action: string;
    escape_corrective_action?: string;  // CR-7354
    escape_preventive_action?: string;  // CR-7354
    impact_assessment: string;
    escape_impact_assessment?: string;  // CR-7353
    // 8D: Impact on Similar Products
    impact_on_similar_products?: string;
    similar_products_affected?: boolean;
    // 8D: Training
    training_required: boolean;
    training_details?: string;
    // 8D: Documentation Updates
    doc_process_flow_updated?: boolean;
    doc_control_plan_updated?: boolean;
    doc_fmea_updated?: boolean;
    doc_work_instructions_updated?: boolean;
    doc_other_updated?: string;
    documentation_references: string;
    corrective_action_notes: string;
    // Customer review 2026-07: 8D extensions
    commonised_corrective_action?: number | null;
    horizontal_deployment_parts?: string;
    system_learning?: string;  // CR-7355: 8D D7 System Learning
    // W7: the CAR's completion date(s).
    action_completion_date?: string | null;
    escape_action_completion_date?: string | null;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_car/`, data),

  // Customer review 2026-07: live repeat-issue lookup for the create form
  ncrRepeatCheck: (params: { part_number: string; fault_group: number }): Promise<AxiosResponse<{ is_repeat: boolean; previous_count: number; last_ncr_id: number | null; last_ncr_number: string | null; last_ncr_date: string | null }>> =>
    api.get('/quality/ncrs/repeat-check/', { params }),

  // Customer review 2026-07: current stock snapshot for the containment block
  ncrPartStock: (params: { part_number: string }): Promise<AxiosResponse<{ stock_quantity: number | null; warehouses: Array<{ warehouse_code: string; stock_quantity: string }> }>> =>
    api.get('/quality/ncrs/part-stock/', { params }),

  rejectNCR: (id: number, rejection_reason: string): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reject_ncr/`, { rejection_reason }),

  approveRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/approve_rejection/`),

  declineRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/decline_rejection/`),

  reopenNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reopen_ncr/`),

  confirmCollection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-collection/`),

  confirmCredit: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-credit/`),

  // NCR claim workflows (collection, credit, replacement): qualityClaims.ts
  ...qualityClaimsAPI,

  // NCR Supporting Resources
  getNCRDocuments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRDocument[] }>> =>
    api.get('/quality/ncr-documents/', { params: { ncr: ncrId } }),

  uploadNCRDocument: (data: FormData): Promise<AxiosResponse<NCRDocument>> =>
    api.post('/quality/ncr-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteNCRDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/ncr-documents/${id}/`),

  getNCRComments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRComment[] }>> =>
    api.get('/quality/ncr-comments/', { params: { ncr: ncrId } }),

  addNCRComment: (ncrId: number, comment: string, is_internal: boolean): Promise<AxiosResponse<NCRComment>> =>
    api.post('/quality/ncr-comments/', { ncr: ncrId, comment, is_internal }),

  // T11 (QUALITY-NCR-REVIEW, 30 Sep 2026): the log is paginated (20 a page);
  // reading one page cut a long NCR's history off at the oldest entries. Walks
  // every page, so the NCR page and the print both get the whole log.
  getNCRActionLogs: async (ncrId: number): Promise<{ data: { count: number; results: NCRActionLog[] } }> => {
    const results = await fetchAllPages<NCRActionLog>((page) =>
      api.get('/quality/ncr-logs/', { params: { ncr: ncrId, page, page_size: 100 } }));
    return { data: { count: results.length, results } };
  },

  getNCRTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.get('/quality/ncr-technical-reviews/', { params: { ncr: ncrId } }),

  createTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post('/quality/ncr-technical-reviews/', { ncr: ncrId }),

  completeTechnicalReview: (id: number, review_notes: string, review_outcome: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/complete_review/`, { review_notes, review_outcome }),

  rejectTechnicalReview: (id: number, rejection_reason: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/reject_review/`, { rejection_reason }),

  // Inspections
  getInspections: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: InspectionRecord[] }>> =>
    api.get('/quality/inspections/', { params }),

  getPassRate: (): Promise<AxiosResponse<InspectionPassRate>> =>
    api.get('/quality/inspections/pass_rate/'),

  // Concessions (CR-3470)
  getConcessions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Concession[] }>> =>
    api.get('/quality/concessions/', { params }),

  getConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.get(`/quality/concessions/${id}/`),

  createConcession: (data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.post('/quality/concessions/', data),

  updateConcession: (id: number, data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.patch(`/quality/concessions/${id}/`, data),

  deleteConcession: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concessions/${id}/`),

  getConcessionStats: (params?: Record<string, string | number>): Promise<AxiosResponse<ConcessionDashboardStats>> =>
    api.get('/quality/concessions/dashboard_stats/', { params }),

  // Concession Workflow Actions
  submitConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/submit/`),

  markConcessionUnderReview: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/mark_under_review/`),

  approveConcession: (id: number, data: {
    review_notes?: string;
    quantity_approved?: number;
    valid_until?: string;
    // CR-6510: the legacy "Approval Process" block.
    qn_mrb_reference?: string;
    approver_name?: string;
    approver_position?: string;
    approver_department?: string;
    customer_response_date?: string;
  }): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/approve/`, data),

  rejectConcession: (id: number, rejection_reason: string): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/reject/`, { rejection_reason }),

  closeConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/close/`),

  acceptConcessionRisk: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/accept_risk/`),

  // Concession Supporting Resources
  getConcessionDocuments: (concessionId: number): Promise<AxiosResponse<ConcessionDocument[]>> =>
    api.get(`/quality/concessions/${concessionId}/documents/`),

  uploadConcessionDocument: (data: FormData): Promise<AxiosResponse<ConcessionDocument>> =>
    api.post('/quality/concession-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteConcessionDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concession-documents/${id}/`),

  // CR-6510: colleagues and departments this user may ask to look at a
  // comment. Scoped server-side to the caller's own company.
  getConcessionMentionOptions: (
    concessionId: number,
  ): Promise<AxiosResponse<ConcessionMentionOptions>> =>
    api.get(`/quality/concessions/${concessionId}/mention-options/`),

  getConcessionComments: (concessionId: number): Promise<AxiosResponse<ConcessionComment[]>> =>
    api.get(`/quality/concessions/${concessionId}/comments/`),

  addConcessionComment: (
    concessionId: number,
    comment: string,
    is_internal: boolean,
    // CR-6510: colleagues / departments asked to look. Anything outside the
    // caller's own company is dropped server-side.
    mentions?: { mentioned_user_ids?: number[]; mentioned_departments?: string[] },
  ): Promise<AxiosResponse<ConcessionComment>> =>
    api.post(`/quality/concessions/${concessionId}/add_comment/`, {
      comment,
      is_internal,
      ...(mentions?.mentioned_user_ids?.length
        ? { mentioned_user_ids: mentions.mentioned_user_ids } : {}),
      ...(mentions?.mentioned_departments?.length
        ? { mentioned_departments: mentions.mentioned_departments } : {}),
    }),

  getConcessionActionLog: (concessionId: number): Promise<AxiosResponse<ConcessionActionLog[]>> =>
    api.get(`/quality/concessions/${concessionId}/action_log/`),

  // Quality Config (admin-managed dropdown options)
  getSourcesOfRejection: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SourceOfRejection[] }>> =>
    api.get('/quality/sources-of-rejection/', { params }),

  createSourceOfRejection: (data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.post('/quality/sources-of-rejection/', data),

  updateSourceOfRejection: (id: number, data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.patch(`/quality/sources-of-rejection/${id}/`, data),

  deleteSourceOfRejection: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/sources-of-rejection/${id}/`),

  // Customer review 2026-07: commonised corrective actions master list
  getCommonisedCorrectiveActions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CommonisedCorrectiveAction[] }>> =>
    api.get('/quality/commonised-corrective-actions/', { params }),

  createCommonisedCorrectiveAction: (data: Partial<CommonisedCorrectiveAction>): Promise<AxiosResponse<CommonisedCorrectiveAction>> =>
    api.post('/quality/commonised-corrective-actions/', data),

  updateCommonisedCorrectiveAction: (id: number, data: Partial<CommonisedCorrectiveAction>): Promise<AxiosResponse<CommonisedCorrectiveAction>> =>
    api.patch(`/quality/commonised-corrective-actions/${id}/`, data),

  deleteCommonisedCorrectiveAction: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/commonised-corrective-actions/${id}/`),

  getFaultGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeGroup[] }>> =>
    api.get('/quality/fault-groups/', { params }),

  createFaultGroup: (data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.post('/quality/fault-groups/', data),

  updateFaultGroup: (id: number, data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.patch(`/quality/fault-groups/${id}/`, data),

  deleteFaultGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-groups/${id}/`),

  getFaultCategories: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeCategory[] }>> =>
    api.get('/quality/fault-categories/', { params }),

  createFaultCategory: (data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.post('/quality/fault-categories/', data),

  updateFaultCategory: (id: number, data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.patch(`/quality/fault-categories/${id}/`, data),

  deleteFaultCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-categories/${id}/`),

  // CR-5333: Approval Groups + Accreditations.
  getApprovalGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ApprovalGroup[] }>> =>
    api.get('/quality/approval-groups/', { params }),

  createApprovalGroup: (data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.post('/quality/approval-groups/', data),

  updateApprovalGroup: (id: number, data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.patch(`/quality/approval-groups/${id}/`, data),

  deleteApprovalGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/approval-groups/${id}/`),

  getAccreditations: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Accreditation[] }>> =>
    api.get('/quality/accreditations/', { params }),

  createAccreditation: (data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.post('/quality/accreditations/', data),

  updateAccreditation: (id: number, data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.patch(`/quality/accreditations/${id}/`, data),

  // CR-5534: Upload (or replace) the accreditation's logo. Separate from
  // updateAccreditation because file uploads require multipart/form-data.
  uploadAccreditationLogo: (id: number, file: File): Promise<AxiosResponse<Accreditation>> => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.patch(`/quality/accreditations/${id}/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAccreditation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/accreditations/${id}/`),

  // Approval Register matrix, supplier accreditations and their
  // certificates: qualityApprovals.ts
  ...qualityApprovalsAPI,

  // CR-5887 (TEMPORARY — remove after testing): fire the Daily Quality
  // Summary email immediately instead of waiting for the 07:00 UTC beat.
  // Pass `to` to send only to that address; omit to send to all recipients.
  triggerDailyQualitySummary: (
    to?: string,
  ): Promise<AxiosResponse<DailyQualitySummaryTriggerResult>> =>
    api.post('/quality/daily-summary/send-now/', to ? { to } : {}),
};
